import React from 'react';
import CustomLink, { CustomLinkProps } from 'components/shared/CustomLink';
import styles from './styleConstants.module.css';

export const colors = {
  d_red: '#F50000',
  black: '#000000',
  white: '#FFFFFF',
} as const;

export type Color = keyof typeof colors;

export const { d_red, black, white } = colors;

const cx = (...parts: (string | false | undefined)[]) =>
  parts.filter(Boolean).join(' ');

interface TypographyProps {
  $color?: Color;
  $uppercase?: boolean;
  as?: React.ElementType;
}

type TextProps = TypographyProps &
  Omit<React.HTMLAttributes<HTMLElement>, 'color'>;

export const Heading = React.forwardRef<HTMLHeadingElement, TextProps>(
  function Heading({ $color, $uppercase, as, className, children, ...rest }, ref) {
    const Tag: any = as || 'h2';
    return (
      <Tag
        ref={ref}
        className={cx(styles.heading, className)}
        data-color={$color}
        data-uppercase={$uppercase === false ? 'false' : undefined}
        {...rest}
      >
        {children}
      </Tag>
    );
  },
);

export const BannerText = React.forwardRef<HTMLHeadingElement, TextProps>(
  function BannerText({ $color, $uppercase, as, className, children, ...rest }, ref) {
    const Tag: any = as || 'h2';
    return (
      <Tag
        ref={ref}
        className={cx(styles.bannerText, className)}
        data-color={$color}
        data-uppercase={$uppercase === false ? 'false' : undefined}
        {...rest}
      >
        {children}
      </Tag>
    );
  },
);

export const SubHeading1 = React.forwardRef<HTMLHeadingElement, TextProps>(
  function SubHeading1({ $color, $uppercase, as, className, children, ...rest }, ref) {
    const Tag: any = as || 'h3';
    return (
      <Tag
        ref={ref}
        className={cx(styles.subHeading1, className)}
        data-color={$color}
        data-uppercase={$uppercase ? 'true' : undefined}
        {...rest}
      >
        {children}
      </Tag>
    );
  },
);

export const SubHeading2 = React.forwardRef<HTMLHeadingElement, TextProps>(
  function SubHeading2({ $color, $uppercase, as, className, children, ...rest }, ref) {
    const Tag: any = as || 'h4';
    return (
      <Tag
        ref={ref}
        className={cx(styles.subHeading2, className)}
        data-color={$color}
        data-uppercase={$uppercase ? 'true' : undefined}
        {...rest}
      >
        {children}
      </Tag>
    );
  },
);

export const Body1 = React.forwardRef<HTMLParagraphElement, TextProps>(
  function Body1({ $color, $uppercase, as, className, children, ...rest }, ref) {
    const Tag: any = as || 'p';
    return (
      <Tag
        ref={ref}
        className={cx(styles.body1, className)}
        data-color={$color}
        data-uppercase={$uppercase ? 'true' : undefined}
        {...rest}
      >
        {children}
      </Tag>
    );
  },
);

export const Body2 = React.forwardRef<HTMLParagraphElement, TextProps>(
  function Body2({ $color, $uppercase, as, className, children, ...rest }, ref) {
    const Tag: any = as || 'p';
    return (
      <Tag
        ref={ref}
        className={cx(styles.body2, className)}
        data-color={$color}
        data-uppercase={$uppercase ? 'true' : undefined}
        {...rest}
      >
        {children}
      </Tag>
    );
  },
);

export const LinkButton = React.forwardRef<
  HTMLAnchorElement,
  CustomLinkProps & { className?: string }
>(function LinkButton({ className, children, ...rest }, ref) {
  return (
    <CustomLink ref={ref} className={cx(styles.linkButton, className)} {...rest}>
      {children}
    </CustomLink>
  );
});
